import type { Ssm5Dtc, Ssm5DtcModule } from '@packages/domain/ssm5';

import { Button, Icon } from '@packages/ui/shared';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ssm5FreezeFramesPath } from '../helpers/ssm5-paths';
import { ssm5ControlStyles } from '../ssm5.styles';
import { Ssm5Card } from './ssm5-card';
import { Ssm5DtcRow } from './ssm5-dtc-row';

const MODULE_ICON_SIZE = 16;
const OPEN_ICON_SIZE = 24;

/** Props for {@link Ssm5DtcModuleCard}. */
export type Ssm5DtcModuleCardProps = {
    /** The module and the DTCs to show for it. */
    module: Ssm5DtcModule;
    /** Called with the system of the module when its open button is pressed. */
    onOpen: (system: string) => void;
    /** The VIN of the session whose DTCs the card shows. */
    vin: string;
};

/**
 * A module of the DTC panel: a card titled with the module, marked as connected with errors, with a row
 * per DTC and a button that opens the module.
 *
 * @param props - {@link Ssm5DtcModuleCardProps}
 * @returns the card
 */
export function Ssm5DtcModuleCard({ module, onOpen, vin }: Ssm5DtcModuleCardProps) {
    const { t } = useTranslation();
    const open = useCallback(() => onOpen(module.system), [module.system, onOpen]);

    return (
        <Ssm5Card
            action={
                <Button
                    accessibilityLabel={t('ssm5:dtcs.openModule', { module: module.system })}
                    onPress={open}
                    style={ssm5ControlStyles.iconButton}
                    variant="ghost-blue"
                >
                    <Icon color="blueOne" name="chevron-right" size={OPEN_ICON_SIZE} />
                </Button>
            }
            icon={<Icon color="warningMain" name="error" size={MODULE_ICON_SIZE} />}
            title={module.system}
        >
            {module.dtcs.map((dtc) => (
                <Ssm5DtcRow
                    dtc={dtc}
                    freezeFramesPath={ssm5FreezeFramesPath(vin, { dtcCode: dtc.code, module: module.system })}
                    key={dtcKey(dtc)}
                />
            ))}
        </Ssm5Card>
    );
}

/**
 * A key telling the DTCs of a module apart: everything the row shows, since the read keeps only one of the
 * DTCs of a system that read the same.
 *
 * @param dtc - the DTC
 * @returns its key
 */
function dtcKey(dtc: Ssm5Dtc): string {
    return JSON.stringify(dtc);
}
